import { ref, type Ref } from 'vue';
import { ofetch } from 'ofetch';

import { createSingleton } from '@deepidoo/deesplay/utils';

import { type AuthenticatedResponse } from '../clients/useAuth';
import { authTokenUrl } from '../urls';

export const useSession = createSingleton((): {
	isAuthenticated: Ref<boolean>;
	isRefreshable: Ref<boolean>;
	accessToken: Ref<string | null>;
	refreshToken: Ref<string | null>;
	setSession: (loginResponse: AuthenticatedResponse) => void;
	clearSession: () => void;
	checkSession: () => void;
	refreshSession: () => Promise<boolean>;
} => {
	const isAuthenticated = ref(false);
	const isRefreshable = ref(false);
	const accessToken = ref<string | null>(localStorage.getItem('accessToken'));
	const refreshToken = ref<string | null>(localStorage.getItem('refreshToken'));
	const refreshInFlight = ref<Promise<boolean> | null>(null);

	const setSession = (loginResponse: AuthenticatedResponse): void => {
		localStorage.setItem('accessToken', loginResponse.access_token);
		localStorage.setItem('refreshToken', loginResponse.refresh_token);
		localStorage.setItem('tokenExpiresIn', String(loginResponse.expires_in));
		localStorage.setItem('tokenCreatedAt', String(loginResponse.created_at));
		isAuthenticated.value = true;
		isRefreshable.value = true;
		accessToken.value = loginResponse.access_token;
		refreshToken.value = loginResponse.refresh_token;
	};

	const clearSession = (): void => {
		localStorage.removeItem('accessToken');
		localStorage.removeItem('refreshToken');
		localStorage.removeItem('tokenExpiresIn');
		localStorage.removeItem('tokenCreatedAt');
		isAuthenticated.value = false;
		isRefreshable.value = false;
		accessToken.value = null;
		refreshToken.value = null;
	};

	const checkSession = (): void => {
		const tokenExpiresIn = Number(localStorage.getItem('tokenExpiresIn'));
		const tokenCreatedAt = Number(localStorage.getItem('tokenCreatedAt'));
		accessToken.value = localStorage.getItem('accessToken');
		refreshToken.value = localStorage.getItem('refreshToken');
		isAuthenticated.value = accessToken.value != null && refreshToken.value != null && (Math.round(Date.now() / 1000) < (tokenCreatedAt + tokenExpiresIn));
		isRefreshable.value = accessToken.value != null && refreshToken.value != null;
	};

	const refreshSession = async (): Promise<boolean> => {
		checkSession();
		if (!isRefreshable.value) {
			return false;
		}
		if (!refreshInFlight.value) {
			refreshInFlight.value = (async (): Promise<boolean> => {
				try {
					const response = await ofetch<AuthenticatedResponse>(authTokenUrl(), {
						method: 'POST',
						body: {
							grant_type: 'refresh_token',
							refresh_token: refreshToken.value
						},
						onRequest: ({ options }) => {
							options.headers.set('X-Client-Id', import.meta.env.VITE_API_CLIENT_ID);
						}
					});
					setSession(response);
					return true;
				}
				catch {
					return false;
				}
				finally {
					refreshInFlight.value = null;
				}
			})();
		}
		return refreshInFlight.value;
	};

	return {
		isAuthenticated,
		isRefreshable,
		accessToken,
		refreshToken,
		setSession,
		clearSession,
		checkSession,
		refreshSession
	};
});
